import { trpcVanilla } from "@/lib/trpc/vanilla";

export type NotificationData = {
  type?: string;
  formId?: string;
  clientId?: string;
  responseId?: string;
  leadId?: string;
  leadResponseId?: string;
  workoutId?: string;
  workoutLogId?: string;
  taskId?: string;
};

type NotificationDataRaw = Record<string, unknown>;

const normalizeNotificationData = (
  raw: NotificationDataRaw,
): NotificationData => ({
  type: raw.type as string | undefined,
  formId: (raw.formId ?? raw.form_id) as string | undefined,
  clientId: (raw.clientId ?? raw.client_id) as string | undefined,
  responseId: (raw.responseId ?? raw.response_id) as string | undefined,
  leadId: (raw.leadId ?? raw.lead_id) as string | undefined,
  leadResponseId: (raw.leadResponseId ?? raw.lead_response_id ?? raw.id) as
    | string
    | undefined,
  workoutId: (raw.workoutId ?? raw.workout_id) as string | undefined,
  workoutLogId: (raw.workoutLogId ?? raw.workout_log_id) as string | undefined,
  taskId: (raw.taskId ?? raw.task_id) as string | undefined,
});

export type NotificationNavigation =
  | {
      to: "/clients/$clientId";
      params: { clientId: string };
    }
  | {
      to: "/clients/$clientId/checkin";
      params: { clientId: string };
    }
  | {
      to: "/clients/$clientId/checkin/$formId/$responseId";
      params: { clientId: string; formId: string; responseId: string };
    }
  | {
      to: "/leads/$leadId";
      params: { leadId: string };
    };

export const parseNotificationData = (
  rawData: unknown,
): NotificationData | null => {
  if (!rawData) {
    return null;
  }
  if (typeof rawData === "string") {
    try {
      return JSON.parse(rawData) as NotificationData;
    } catch {
      return null;
    }
  }
  if (typeof rawData === "object") {
    return normalizeNotificationData(rawData as NotificationDataRaw);
  }
  return null;
};

const resolveCheckInResponseId = async (
  data: NotificationData,
): Promise<NotificationData> => {
  if (
    (data.type !== "check-in" && data.type !== "form") ||
    !data.clientId ||
    !data.formId ||
    data.responseId
  ) {
    return data;
  }

  try {
    const responseId = await trpcVanilla.checkins.latestResponseId.query({
      clientId: data.clientId,
      formId: data.formId,
    });
    if (!responseId) {
      return data;
    }
    return { ...data, responseId };
  } catch (error) {
    console.error("Failed to fetch check-in response", error);
    return data;
  }
};

export const resolveNotificationNavigation = async (
  rawData: unknown,
): Promise<NotificationNavigation | null> => {
  const data = parseNotificationData(rawData);
  if (!data) {
    return null;
  }

  switch (data.type) {
    case "workout":
      if (data.clientId) {
        return {
          to: "/clients/$clientId",
          params: { clientId: data.clientId },
        };
      }
      return null;
    case "package_purchase":
    case "payment_failed":
    case "subscription_cancel_scheduled":
      if (data.clientId) {
        return {
          to: "/clients/$clientId",
          params: { clientId: data.clientId },
        };
      }
      return null;
    case "check-in":
    case "form":
      if (data.clientId && !data.formId) {
        return {
          to: "/clients/$clientId/checkin",
          params: { clientId: data.clientId },
        };
      }
      break;
    case "lead_response":
      if (data.leadId) {
        return {
          to: "/leads/$leadId",
          params: { leadId: data.leadId },
        };
      }
      break;
    case "coach_app_launch":
      // Opens the coach app dialog — no navigation needed
      return null;
    case "task_assigned":
      return null;
    default:
      break;
  }

  console.log("Resolving notification navigation for data", data);

  const resolvedData = await resolveCheckInResponseId(data);

  if (resolvedData.type === "workout" && resolvedData.clientId) {
    return {
      to: "/clients/$clientId",
      params: { clientId: resolvedData.clientId },
    };
  }

  if (
    resolvedData.type === "check-in" &&
    resolvedData.clientId &&
    resolvedData.formId
  ) {
    if (resolvedData.responseId) {
      return {
        to: "/clients/$clientId/checkin/$formId/$responseId",
        params: {
          clientId: resolvedData.clientId,
          formId: resolvedData.formId,
          responseId: resolvedData.responseId,
        },
      };
    }

    return {
      to: "/clients/$clientId/checkin",
      params: { clientId: resolvedData.clientId },
    };
  }

  if (resolvedData.clientId && resolvedData.formId && resolvedData.responseId) {
    return {
      to: "/clients/$clientId/checkin/$formId/$responseId",
      params: {
        clientId: resolvedData.clientId,
        formId: resolvedData.formId,
        responseId: resolvedData.responseId,
      },
    };
  }

  if (resolvedData.leadId) {
    return {
      to: "/leads/$leadId",
      params: { leadId: resolvedData.leadId },
    };
  }

  return null;
};
